/* recotizarBudget.js — lógica pura del diálogo "Recotizar" de admin/usuarios.
 *
 * Vive aparte del componente para probarla sin montar useCarCalculator.
 * Plan: backend/docs/plans/admin-recotizar-placa-seguro-manual.md
 * (D-ADM-2 a D-ADM-9, §4.2 y copys del §6).
 */
import { mensajeDeError } from '../../../../services/adminRecotizacionApi';
import { payloadSeguroManual } from '../../../../Utils/seguroManual';

/* Valor de `rodac.insurance` mientras el seguro manual está aplicado: con él
 * pasa canContinue() del hook sin que haya una opción de RODAC elegida. */
export const SEGURO_MANUAL = 'manual';

export const SEGURO_MANUAL_VACIO = { company: '', montos: {}, folios: {}, foliosAuto: {} };

/* Copys del §6. */
export const COPY_BLOQUEO = {
    poliza_emitida: 'Esta cotización ya tiene póliza emitida. Recotízala desde el expediente.',
    cotizador3: 'Esta cotización es de la Calculadora 3 (Trim) y no se puede recotizar aquí.',
    contrato_en_cartera: 'Esta cotización ya es un contrato en Cartera y no se puede recotizar.',
    no_es_cotizacion: 'Este registro no salió del cotizador (importado o alta manual) y no se puede recotizar.',
};
export const COPY_EXPEDIENTE = 'Tiene expediente abierto: al recotizar cambia la cotización con la que se está trabajando.';

const FALLBACK_POST = 'No se pudo recotizar la cotización. Intenta de nuevo.';

/* Mismas etiquetas que RecotizacionDialog (llaves de canContinue()). */
export const ETIQUETAS_CAMPOS = {
    name: 'Nombre', type: 'Tipo vehículo', type_coverage: 'Tipo cobertura',
    car_type_select: 'Tipo auto', condition: 'Condición', model_year: 'Año',
    brand: 'Marca', model_name: 'Modelo', model_version: 'Versión',
    zip: 'Código postal', colonia: 'Colonia', municipio: 'Municipio',
    ciudad: 'Ciudad', estado: 'Estado', amount: 'Precio',
    estado_placa: 'Estado placa', plazo: 'Plazo', enganche: 'Enganche',
    type_engine: 'Tipo motor', typeRegime: 'Régimen', residualValue: 'Valor residual',
    insurance: 'Seguro',
};

export const formatoMoneda = (n, decimales = 2) =>
    `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })}`;

/* Plazo elegido en meses (NaN si no hay). Misma cuenta que `term` en buildSubmitPayload(). */
export const plazoActual = (rodac) =>
    +rodac?.payment_configurations?.find((c) => +c.id === +rodac.payment_configuration_id)?.configuration;

/* RODAC ya contestó y no dio seguro en NINGÚN plazo (D-ADM-8, hallazgo 13).
 * Si un solo plazo trae opciones es el caso 'plazo' y el manual no sale. */
export function rodacSinSeguro(calculatorState) {
    const r = calculatorState?.rodac;
    return Boolean(
        r?.fetched_insurances &&
        !calculatorState.loader &&
        Array.isArray(r.plazosInsurances) &&
        r.plazosInsurances.length > 0 &&
        r.plazosInsurances.every((a) => !a || a.length === 0)
    );
}

/* "Aplicado" se lee del hook y no de un flag aparte: si el admin cambia el
 * plazo, el formulario limpia `insurance` y el manual deja de estar aplicado solo. */
export const seguroManualAplicado = (calculatorState) =>
    calculatorState?.rodac?.insurance === SEGURO_MANUAL;

/* El budget del GET listo para el hook. El hook les hace .toLowerCase() a estos
 * campos y un null tumbaría la página entera; el backend ya los manda (§4.2),
 * esto es solo red de seguridad con los mismos defaults. */
export function budgetParaCalculadora(budget) {
    if (!budget) return null;
    const vehicle = budget.vehicle || {};
    return {
        ...budget,
        state_plate_id: String(budget.state_plate_id || 'CDMX'),
        vehicule_type: String(budget.vehicule_type || 'Nuevo'),
        engine: String(budget.engine || 'Gasolina/Diesel'),
        vehicle: {
            ...vehicle,
            brand: String(vehicle.brand ?? ''),
            model: String(vehicle.model ?? ''),
            description: String(vehicle.description ?? ''),
            typeAutoOrPickup: String(vehicle.typeAutoOrPickup || 'AUTO'),
        },
    };
}

/* Body del POST: el payload del cotizador con el seguro manual encima si está
 * aplicado (`manual` = { plazo, company, montos, folios, foliosAuto }). Nunca lleva `secret`:
 * lo pone el backend (D-ADM-2). */
export function armarPayloadRecotizacion(base, manual = null) {
    const payload = { ...(base || {}) };
    if (manual) Object.assign(payload, payloadSeguroManual(manual));
    delete payload.secret;
    return payload;
}

/* "Faltan: …" con las llaves que dejó canContinue() en errorsArray. */
export function textoFaltantes(campos) {
    const nombres = [...new Set((campos || []).map((c) => ETIQUETAS_CAMPOS[c] || c))];
    return nombres.length
        ? `Faltan: ${nombres.join(', ')}`
        : 'Verifica que todos los campos estén completos.';
}

/* Toast de campos incompletos. Si lo que falta es el seguro y la tarjeta del
 * seguro manual está sin usar, dice qué botón pulsar: "Faltan: Seguro" solo
 * no se entendía (prueba del dueño, 30-sep). */
export function avisoDeFaltantes(campos, faltaUsarManual = false) {
    const lista = campos || [];
    if (!faltaUsarManual || !lista.includes('insurance')) {
        return { summary: 'Campos incompletos', detail: textoFaltantes(lista) };
    }
    const otros = lista.filter((c) => c !== 'insurance');
    const paso = 'En «Seguro manual» pulsa «Usar este seguro» y después COTIZAR.';
    return {
        summary: 'Falta usar el seguro manual',
        detail: otros.length ? `${textoFaltantes(otros)}. ${paso}` : paso,
    };
}

export function textoBloqueo(bloqueo) {
    if (!bloqueo) return '';
    return COPY_BLOQUEO[bloqueo.motivo] || bloqueo.mensaje || 'Esta cotización no se puede recotizar.';
}

export const tieneExpediente = (avisos) => Array.isArray(avisos) && avisos.includes('tiene_expediente');

/* Renta mensual con IVA del resumen del motor. Financiero la manda como
 * [sin IVA, con IVA] y puro como un solo texto ("12,345.67"). */
export function rentaDeResumen(resumen) {
    const raw = resumen?.['Pago Mensual Total (incl IVA)'];
    const valor = Array.isArray(raw) ? raw[raw.length - 1] : raw;
    if (valor === null || valor === undefined || valor === '') return null;
    const n = typeof valor === 'number' ? valor : Number(String(valor).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
}

export function textoExito(res) {
    const renta = rentaDeResumen(res?.resumen);
    return renta === null
        ? 'Recotización aplicada'
        : `Recotización aplicada · nueva renta ${formatoMoneda(renta)}/mes`;
}

/* `campos` del 422 seguro_manual_invalido ('montos.36', 'folios.36', 'company',
 * 'mezcla', 'plazo') → la forma de validarSeguroManual().errores. */
export function erroresDesdeCampos(campos) {
    const errores = { montos: {}, folios: {} };
    Object.entries(campos || {}).forEach(([llave, msg]) => {
        const texto = Array.isArray(msg) ? msg[0] : msg;
        const porPlazo = /^(montos|folios)\.(\d+)$/.exec(llave);
        if (porPlazo) errores[porPlazo[1]][Number(porPlazo[2])] = texto;
        else errores[llave] = texto;
    });
    return errores;
}

/* Error del POST → { severity, summary, detail } para el toast, más `bloqueo`
 * (409) o `campos` (422 del seguro manual) cuando toca. */
export function errorDeRecotizacion(err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const cuerpo = data && typeof data === 'object' ? data : {};

    if (status === 409) {
        const bloqueo = {
            motivo: cuerpo.bloqueo?.motivo || cuerpo.motivo || cuerpo.error || null,
            mensaje: cuerpo.bloqueo?.mensaje || cuerpo.message || null,
        };
        return { severity: 'warn', summary: 'No se puede recotizar', detail: textoBloqueo(bloqueo), bloqueo };
    }

    if (status === 422 && cuerpo.error === 'seguro_manual_invalido') {
        return {
            severity: 'warn',
            summary: 'Revisa el seguro manual',
            detail: mensajeDeError(err, 'El seguro manual tiene datos inválidos.'),
            campos: erroresDesdeCampos(cuerpo.campos),
        };
    }

    if (status === 422 && cuerpo.min_sugerido !== undefined && cuerpo.min_sugerido !== null) {
        const minimo = Number(cuerpo.min_sugerido);
        const extra = Number.isFinite(minimo) ? ` Mínimo sugerido: ${formatoMoneda(minimo)}.` : '';
        return {
            severity: 'warn',
            summary: 'Pago inicial insuficiente',
            detail: `${mensajeDeError(err, 'El pago inicial es menor al mínimo.')}${extra}`,
        };
    }

    if (status === 422 && cuerpo.max_sugerido !== undefined && cuerpo.max_sugerido !== null) {
        const maximo = Number(cuerpo.max_sugerido);
        const extra = Number.isFinite(maximo) ? ` Máximo: ${formatoMoneda(maximo)}.` : '';
        return {
            severity: 'warn',
            summary: 'Pago inicial demasiado alto',
            detail: `${mensajeDeError(err, 'El pago inicial no puede ser mayor al precio del vehículo.')}${extra}`,
        };
    }

    if (status === 422) {
        return { severity: 'warn', summary: 'Datos inválidos', detail: mensajeDeError(err, 'Revisa los datos de la cotización.') };
    }

    if (status === 401 || status === 403) {
        return { severity: 'error', summary: 'Sin permiso', detail: mensajeDeError(err, 'No tienes permiso para recotizar.') };
    }

    // El motor responde 500 con el texto "error": no se le enseña eso al admin.
    if (status >= 500) {
        return { severity: 'error', summary: 'No se pudo recotizar', detail: cuerpo.message || FALLBACK_POST };
    }

    return { severity: 'error', summary: 'No se pudo recotizar', detail: mensajeDeError(err, FALLBACK_POST) };
}
